import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, authFetch } from '../utils/api';
import { won, balColor } from './ClientScopeCombo';
import { useIsMobile, hScroll } from '../utils/useMobile';
import CancelAgencyInviteModal from './CancelAgencyInviteModal';
import CancelAgencyShareLinkModal from './CancelAgencyShareLinkModal';
import CancelAgencyJoinRequests from './CancelAgencyJoinRequests';

// ═══ 말소대행 거래처 탭 (W2 계층형 개편) ═══
// 정본: erp/app/docs/settlement-ledger/clients-crud-redesign.md §4·§5·§6 + 시안 clients-tab-w2-mockup.html
//
// 목표 모델 — 거래처의 진실 = billing_accounts(direct|broker) + work_units 계층.
// 이 탭이 그 계층의 유일한 쓰기 화면이고, 정산 탭은 같은 계층의 읽기(스코프 선택)다.
//   · 대행사(broker) 그룹 헤더 행 = 계정 자체 (이름/사업자번호/활성/메모/이월)
//   · 들여쓴 하위 행 + 직거래 평면 행 = work_units (이름/사업자번호/연결회사/단가/이월)
//   · 하위업체 편집 폼에는 계정 필드가 아예 없다 — 상위 오염(결함 ①) 을 UI 에서도 차단.
//   · 삭제는 잔액 0 일 때만 노출, 그 외엔 이유 + 비활성 대안을 그 자리에서 제시 (결함 ②).
//   · 소속 이동/승격은 확인 모달 경유 (PUT /work-units, POST /billing-accounts/:id/promote).
//
// 데이터 원천 2개를 조합한다:
//   GET /agency-clients                    — 단위 상세 (단가/연결회사/메모/이월/사업자번호)
//   GET /billing-accounts?includeUnits=1   — 계층 + 잔액 (계정·단위)
// 계층 골격은 계정 응답에서 만들고, 단위 상세는 id 로 머지한다.
function CancelAgencyClients({ showToast, user, agencies, isCancelAgency }) {
  var navigate = useNavigate();
  var isMobile = useIsMobile();   // ≤768px — 데스크탑 렌더는 이 분기 밖에서 절대 불변
  var fmt = function(n){ return (parseInt(n,10)||0).toLocaleString(); };

  var [agencyId, setAgencyId] = useState('');   // admin/doc_agency 선택값 (cancel_agency 는 backend override)
  var [clients, setClients] = useState([]);
  var [accounts, setAccounts] = useState([]);
  var [linkable, setLinkable] = useState([]);
  var [q, setQ] = useState('');
  var [needBizOnly, setNeedBizOnly] = useState(false);  // 발행대상 사업자번호 미입력만 보기
  // 그룹 펼침 — **기본 접힘** (Jin 8/27 — 기본 5개 노출이 화면을 복잡하게 해 전부 접기로 번복.
  // 8/24 의 "접힘이면 펼침 가능이 인지 안 됨" 우려는 그 뒤 caret 을 크게·채움으로 키워 해소됨).
  // openMap 은 "펼친 것"만 기록한다(true = 펼침). 검색·필터 중엔 종전대로 강제 펼침.
  var [openMap, setOpenMap] = useState({});
  // 한 번에 다 쏟아내면 264곳이 직거래를 밀어낸다 → 처음 5곳만, 나머지는 [더 보기].
  var [moreMap, setMoreMap] = useState({});     // gid → 현재 표시 개수 (미지정 = GROUP_PAGE)
  var [sortKey, setSortKey] = useState(null);   // null = 기본(활성 우선 + 이름). 헤더 클릭으로 지정
  var [sortDir, setSortDir] = useState('asc');
  var [hotCaret, setHotCaret] = useState(null); // 호버 중인 그룹 caret id (인라인 스타일이라 :hover 를 못 씀)
  var [editing, setEditing] = useState(null);   // {type:'unit'|'account', id}
  var [form, setForm] = useState(null);
  var [menuFor, setMenuFor] = useState(null);   // ⋯ 메뉴 {type, id}
  var [add, setAdd] = useState(null);           // + 거래처 추가 모달 폼
  var [move, setMove] = useState(null);         // 소속 이동 확인 모달
  var [promote, setPromote] = useState(null);   // 대행사 승격 확인 모달
  var [invite, setInvite] = useState(null);     // 앱 초대 링크 모달 {id, name} 단위 | {accountId, name} 대행업체 — 미연결·활성만
  var [shareOpen, setShareOpen] = useState(false); // 대표 링크 모달 (YK 대표 링크 2026-11-03)
  var [ocrLoading, setOcrLoading] = useState(false);
  var [panelIn, setPanelIn] = useState(false);  // 데스크탑 편집 패널 슬라이드-인 (마운트 직후 1틱 뒤 true)
  // 도움말 — 작업 유형 탭과 같은 문법(요약 줄 + [도움말] 토글). 데스크탑 펼침 / 모바일 접힘.
  var [guideOpen, setGuideOpen] = useState(function(){ return !isMobile; });

  var canQuery = function(){ return isCancelAgency || !!agencyId; };
  var qs = function(){ return (!isCancelAgency && agencyId) ? ('?agencyId=' + agencyId) : ''; };
  var amp = function(){ return (!isCancelAgency && agencyId) ? ('&agencyId=' + agencyId) : ''; };
  var scopeBody = function(o){ if(!isCancelAgency && agencyId) o.agencyId = parseInt(agencyId,10); return o; };
  // 대표 링크 · 연결 확인 대기 — 말소대행 운영자·admin/관리자만 (서버 routes/agencyShare 가드와 같은 경계: 그 외 role·직원 403).
  //   직원 판정 = CancelAgency.js isStaff 와 같은 식.
  var isStaff = isCancelAgency && !!(user && user.agency_staff);
  var shareRole = !!user && (user.role === 'cancel_agency' || user.role === 'admin' || user.role === '관리자');
  var canShare = canQuery() && shareRole && !isStaff;
  var shareScope = (!isCancelAgency && agencyId) ? String(agencyId) : '';
  var agencyName = ((agencies||[]).filter(function(a){ return isCancelAgency ? a.id === (user && user.agencyId) : String(a.id) === String(agencyId); })[0] || {}).name || '';

  var load = function(){
    if(!canQuery()){ setClients([]); setAccounts([]); setLinkable([]); return; }
    authFetch(API+'/agency-clients'+qs()).then(function(r){return r.json()}).then(function(d){ setClients(Array.isArray(d)?d:[]) }).catch(function(){});
    authFetch(API+'/billing-accounts?includeUnits=1'+amp()).then(function(r){return r.json()}).then(function(d){ setAccounts(Array.isArray(d)?d:[]) }).catch(function(){});
    authFetch(API+'/agency-clients/linkable'+qs()).then(function(r){return r.json()}).then(function(d){ setLinkable(Array.isArray(d)?d:[]) }).catch(function(){});
  };
  useEffect(function(){ load(); }, [agencyId, isCancelAgency]); // eslint-disable-line react-hooks/exhaustive-deps

  var closeAll = function(){ setEditing(null); setForm(null); setMenuFor(null); };
  var setF = function(k,v){ setForm(function(p){ if(!p) return p; var n={}; for(var x in p)n[x]=p[x]; n[k]=v; return n; }); };
  var setA = function(k,v){ setAdd(function(p){ if(!p) return p; var n={}; for(var x in p)n[x]=p[x]; n[k]=v; return n; }); };

  // ── 계층 조립 ────────────────────────────────────────────────────────────
  var clientById = {};
  clients.forEach(function(c){ clientById[c.id] = c; });

  // 단위(계정 응답) + 거래처 상세(agency-clients) 머지. 상세가 없으면 계정 응답 값으로 degrade.
  var mergeUnit = function(u, acc){
    var c = clientById[u.id] || {};
    return {
      type: 'unit',
      id: u.id,
      accountId: acc.id,
      accountName: acc.name,
      accountKind: acc.kind,
      isChild: acc.kind === 'broker',
      name: c.name || u.name || '',
      // 번호의 진실은 단위(분기 B). 상세가 없을 때만 direct 계정 번호로 폴백 — broker 형제 공유 오염 차단.
      bizNo: (c.bizNo === undefined || c.bizNo === null) ? (acc.kind === 'direct' ? (acc.bizNo || '') : '') : c.bizNo,
      companyId: c.companyId || u.companyId || 0,
      companyName: c.companyName || '',
      addr: c.addr || '',   // 말소신청서 위임자 주소 — GET /agency-clients 응답에 이미 포함
      serviceFee: (c.serviceFee !== undefined) ? c.serviceFee : u.serviceFee,
      wonbuFee: c.wonbuFee || 0,
      reissueFee: c.reissueFee || 0,
      issueTaxInvoice: (c.issueTaxInvoice === 0 ? 0 : 1),
      active: ((c.active !== undefined ? c.active : u.active) ? 1 : 0),
      memo: c.memo || '',
      openingBalance: c.openingBalance || 0,
      balance: parseInt(u.balance,10) || 0
    };
  };

  var groups = [], directs = [], legacyRows = [], systemRows = [];
  accounts.forEach(function(a){
    var us = Array.isArray(a.units) ? a.units : [];
    if(a.kind === 'broker'){
      groups.push({
        type: 'account', id: a.id, name: a.name || '', bizNo: a.bizNo || '',
        active: a.active ? 1 : 0, balance: parseInt(a.balance,10) || 0,
        unitsCount: (a.unitsCount != null ? a.unitsCount : us.length),
        units: us.map(function(u){ return mergeUnit(u, a); })
      });
      return;
    }
    // direct — 단위 1:1 페어가 정상. (미지정) 시스템 계정 / 단위 없는 껍데기는 별도 취급.
    if((a.name || '') === '(미지정)'){
      systemRows.push({ type:'system', id:a.id, name:a.name, balance: parseInt(a.balance,10)||0 });
      return;
    }
    if(us.length === 0){
      // 이동 시 잔액을 남긴 옛 자리 = '종전 잔액' 읽기 전용 행. 완납(0)이면 목록에서 감춘다.
      if((parseInt(a.balance,10)||0) !== 0){
        legacyRows.push({ type:'legacy', id:a.id, name:a.name||'', bizNo:a.bizNo||'', balance: parseInt(a.balance,10)||0 });
      }
      return;
    }
    us.forEach(function(u){ directs.push(mergeUnit(u, a)); });
  });

  var byActiveName = function(a,b){
    if(a.active !== b.active) return b.active - a.active;
    return String(a.name||'').localeCompare(String(b.name||''), 'ko');
  };

  // ── 헤더 정렬 ──────────────────────────────────────────────────────────────
  // 계층(대행사 그룹 ↔ 하위 ↔ 직거래)은 유지한 채 **각 묶음 안에서만** 재정렬한다.
  // 전체를 평면으로 섞으면 A안(그룹 펼침)에서 만든 구조가 무너진다.
  // 기본(sortKey=null)은 종전 byActiveName — 비활성 뒤로, 이름 오름차순.
  var SORT_TEXT = {name:'name', bizNo:'bizNo', companyName:'companyName'};
  var SORT_NUM = {serviceFee:'serviceFee', balance:'balance'};
  var cmpBy = function(key, dir){
    var sign = dir === 'desc' ? -1 : 1;
    return function(a,b){
      var r;
      if (SORT_NUM[key]) {
        // 빈 값(0/null)은 방향과 무관하게 뒤로 — 정렬을 바꿔도 "미입력"이 위로 올라오지 않게.
        var av = parseInt(a[key],10)||0, bv = parseInt(b[key],10)||0;
        if (!av !== !bv) return av ? -1 : 1;
        r = av - bv;
      } else {
        var as = String(a[SORT_TEXT[key]]||'').trim(), bs = String(b[SORT_TEXT[key]]||'').trim();
        if (!as !== !bs) return as ? -1 : 1;   // 빈 값 뒤로 (동일 사유)
        r = as.localeCompare(bs, 'ko');
      }
      if (r) return r * sign;
      return String(a.name||'').localeCompare(String(b.name||''), 'ko'); // 동점은 이름으로 고정
    };
  };
  var sorter = sortKey ? cmpBy(sortKey, sortDir) : byActiveName;
  groups.forEach(function(g){ g.units.sort(sorter); });
  groups.sort(sorter);
  directs.sort(sorter);

  // ── 세금계산서 발행 대상인데 사업자번호가 빈 거래처 ────────────────────────
  // 발행 시 사업자번호가 필수라 이 상태로는 계산서가 막힌다 → 배지 + 상단 필터 칩으로 노출.
  var needBiz = function(o){ return !!o.issueTaxInvoice && !String(o.bizNo||'').trim(); };
  var allUnitsFlat = directs.concat(groups.reduce(function(s,g){ return s.concat(g.units); }, []));
  var needBizCount = allUnitsFlat.filter(needBiz).length;
  // 연결 확인 시트 '다른 거래처 고르기' — 미연결·활성 단위 + 소속 계정도 활성(서버 link UNIT_INACTIVE 와 같은 조건). 부제는 서버 후보 label 과 같은 말.
  var inactiveAccIds = accounts.filter(function(a){ return !a.active; }).map(function(a){ return a.id; });
  var joinPickUnits = allUnitsFlat.filter(function(u){ return !u.companyId && u.active && inactiveAccIds.indexOf(u.accountId) < 0; }).map(function(u){
    return { id: u.id, name: u.name, bizNo: u.bizNo, sub: !u.isChild ? '직거래' : (u.name === u.accountName ? '대행사' : u.accountName + ' 하위') };
  }).concat(groups.filter(function(g){
    // 본인 몫(계정 이름과 같은 이름의 단위 — 서버 brokerScope.selfUnitOf)이 아직 없는 활성 대행사 계정. 고르면 {accountId} 로 연결되고
    // 서버가 본인 몫을 만들어 붙인다. 단위 id 와 겹치지 않게 음수 id(-계정 id)로 둔다 (CancelAgencyJoinRequests linkBody).
    return g.active && !g.units.some(function(u){ return u.name === g.name; });
  }).map(function(g){
    return { id: -g.id, name: g.name, bizNo: g.bizNo, sub: '대행사' };
  }));
  // 칩이 사라진 뒤에도 필터가 남아 빈 목록이 되지 않게 — 대상 0 이면 필터 자동 무효화.
  var bizFilterOn = needBizOnly && needBizCount > 0;

  // ── 검색 (일치 구간 하이라이트 — 정산 픽커와 동일 규약) ──────────────────
  var ql = q.trim().toLowerCase();
  var hit = function(o){
    if(!ql) return true;
    return String(o.name||'').toLowerCase().indexOf(ql) >= 0 || String(o.bizNo||'').toLowerCase().indexOf(ql) >= 0;
  };
  var hi = function(name){
    var n = String(name||'');
    if(!ql) return n;
    var i = n.toLowerCase().indexOf(ql);
    if(i < 0) return n;
    return (<span>{n.slice(0,i)}<span style={{color:'#dc2626',fontWeight:700}}>{n.slice(i,i+ql.length)}</span>{n.slice(i+ql.length)}</span>);
  };

  var shownGroups = groups.map(function(g){
    var gHit = hit(g);
    var units = (ql ? (gHit ? g.units : g.units.filter(hit)) : g.units)
      .filter(function(u){ return !bizFilterOn || needBiz(u); });
    // 필터 중에는 대상 하위업체가 있는 대행사만 남긴다 (빈 그룹 헤더 숨김)
    return { g: g, units: units, show: bizFilterOn ? units.length > 0 : (!ql || gHit || units.length > 0) };
  }).filter(function(x){ return x.show; });
  var shownDirects = directs.filter(function(r){ return hit(r) && (!bizFilterOn || needBiz(r)); });
  // 종전 잔액·시스템 행은 거래처 단위가 아니라 필터 대상 밖 → 필터 중 숨김
  var shownLegacy = bizFilterOn ? [] : legacyRows.filter(hit);
  var shownSystem = bizFilterOn ? [] : systemRows.filter(hit);

  var totalUnits = directs.length + systemRows.length + groups.reduce(function(s,g){ return s + g.units.length; }, 0);
  var shownCount = shownDirects.length + shownSystem.length + shownLegacy.length +
    shownGroups.reduce(function(s,x){ return s + x.units.length + 1; }, 0);

  // 레이아웃 확정 직후 측정(useLayoutEffect) — paint 전에 잡아야 첫 프레임부터 틈이 없다.
  useLayoutEffect(function(){
    if (!theadRef.current) return;
    var h = theadRef.current.getBoundingClientRect().height;
    if (h && Math.abs(h - theadH) > 0.5) setTheadH(h);
  });

  var isOpen = function(gid){ return (ql || bizFilterOn) ? true : openMap[gid] === true; };
var collapseGroup = function(gid){
    setOpenMap(function(p){ var n = Object.assign({}, p); n[gid] = false; return n; });
    setMoreMap(function(p){ var n = Object.assign({}, p); delete n[gid]; return n; });
  };
  // caret 접기도 푸터 [접기] 와 같은 결과여야 한다 — 표시 개수를 안 되돌리면
  // 다시 펼쳤을 때 264곳이 그대로 쏟아진다(테스트로 적발).
  var toggleGroup = function(gid){
    if (openMap[gid] === true) { collapseGroup(gid); return; }
    setOpenMap(function(p){ var n = Object.assign({}, p); n[gid] = true; return n; });
  };
  // 검색·필터 중에는 찾으러 온 것이므로 페이지 제한을 걸지 않는다.
  var shownLimit = function(gid){
    if (ql || bizFilterOn) return Infinity;
    return moreMap[gid] || GROUP_PAGE;
  };
  // [더 보기] 1회 = 남은 하위 전부. 20곳씩 끊어 여러 번 누르게 하는 건 번거롭기만 했다(Jin 8/24).
  var showMore = function(gid, total){
    setMoreMap(function(p){
      var n = Object.assign({}, p);
      n[gid] = total;
      return n;
    });
  };
  // 접으면 표시 개수도 처음으로 되돌린다 — 다시 펼쳤을 때 264곳이 그대로 쏟아지지 않게.
  
  // 연결 회사 드롭다운 — linkable + (편집 중 현재 연결값이 목록에 없으면 보존)
  var companyOptions = function(curId, curName){
    var opts = linkable.slice();
    if(curId && !opts.some(function(o){return String(o.id)===String(curId)})) opts = [{id:curId, name:(curName||('#'+curId))}].concat(opts);
    return opts;
  };
  var brokerOptions = groups.map(function(g){ return {id:g.id, name:g.name}; });

  // 편집 대상 — editing.id 로 계층에서 역조회. 데스크탑=오른쪽 슬라이드 패널 / 모바일=풀스크린 시트 공통.
  var editUnitRec = null, editAccRec = null;
  if(editing && form){
    if(editing.type === 'unit'){
      var allUnits = directs.concat(groups.reduce(function(s,g){ return s.concat(g.units); }, []));
      editUnitRec = allUnits.filter(function(u){ return u.id === editing.id; })[0] || null;
    } else if(editing.type === 'account'){
      editAccRec = groups.filter(function(g){ return g.id === editing.id; })[0] || null;
    }
  }

  // 편집 패널 닫기 3경로 중 하나 — ESC. (나머지 둘은 ✕ 버튼 · 배경 클릭)
  // 확인 모달(추가/이동/승격)이 위에 떠 있을 땐 뒤의 패널을 건드리지 않는다.
  useEffect(function(){
    if(!editing) return undefined;
    var on = function(e){ if(e.key === 'Escape' && !add && !move && !promote) closeAll(); };
    window.addEventListener('keydown', on);
    return function(){ window.removeEventListener('keydown', on); };
  }, [editing, add, move, promote]); // eslint-disable-line react-hooks/exhaustive-deps

  // 슬라이드-인 — 마운트 직후 transform 을 바꿔야 transition 이 실제로 돈다(마운트 프레임에선 안 돔).
  var editKey = editing ? (editing.type + ':' + editing.id) : '';
  useEffect(function(){
    if(!editKey){ setPanelIn(false); return undefined; }
    var t = setTimeout(function(){ setPanelIn(true); }, 16);
    return function(){ clearTimeout(t); };
  }, [editKey]);

  // 접근성 — 모션 최소화 설정이면 슬라이드/페이드 자체를 끈다
  var reduceMotion = (typeof window !== 'undefined' && window.matchMedia)
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;

  // ── 스타일 (시안 계열 — navy #1B3A5C / accent #2563EB / bluebg #eff6ff / rail #60a5fa) ──
  // sticky 기준선 — 스크롤 조상(.dash-content)의 padding-top 24 를 상쇄해 헤더 바로 아래에 붙인다.
  // 열 머리글이 먼저 붙고, 그룹 머리글은 그 아래(THEAD_H 만큼)에 2단으로 쌓인다.
  var STICKY_TOP = -24;
  // 열 머리글 높이는 **실측**한다. 상수로 두면 폰트·패딩·줌에 따라 실제 높이와 어긋나
  // 두 sticky 사이에 틈이 생기고 그 틈으로 아래 행이 지나간다(3px 틈 실제 발생, Jin 8/24).
  var theadRef = useRef(null);
  var [theadH, setTheadH] = useState(isMobile ? 36 : 34);
  var TH = {fontSize:isMobile?12:11,color:'#94a3b8',textAlign:'left',fontWeight:600,padding:isMobile?'9px 10px':'8px 12px',borderBottom:'1px solid #e2e8f0',background:'#f1f5f9',whiteSpace:isMobile?'nowrap':undefined,
    position:'sticky', top:STICKY_TOP, zIndex:3};
  var TD = {padding:isMobile?'13px 10px':'9px 12px',borderBottom:'1px solid #f1f5f9',verticalAlign:'middle'};
  var HINT = {color:'#94a3b8'};
  // 모바일 = 3열 축약 (거래처 / 미수 잔액 / 관리). 6열(minWidth 660)을 그대로 두면 390 화면에서
  // 정작 볼 값(미수)과 누를 것(⋯)이 가로 스크롤 밖으로 나간다 — 실측: 보이는 건 이름·사업자번호뿐.
  // 감춘 열(사업자번호·연결 회사·수수료)은 이름 아래 메타 줄로 내려 정보는 그대로 남긴다.
  var COLS = isMobile ? 3 : 6;
  // 한글은 기본 word-break 가 글자 단위라 좁은 칸에서 '가나다무역/상사' 로 끊긴다 → 어절 유지,
  // 어절 하나가 칸보다 길 때만(긴 상호) 강제 분리.
  var nameWrap = isMobile ? {wordBreak:'keep-all', overflowWrap:'anywhere'} : null;
  var metaSt = {display:'block',fontSize:12,color:'#94a3b8',marginTop:3,lineHeight:1.45};
  var metaDot = <span style={{color:'#cbd5e1'}}> · </span>;
  var badge = function(bg, color, border){
    return {display:'inline-block',fontSize:isMobile?12:10.5,fontWeight:700,borderRadius:5,padding:'1px 7px',marginLeft:6,background:bg,color:color,border:border||'none',verticalAlign:1};
  };
  // 가입 회원(회사 연결됨) 배지 (Jin 10/29). 대행업체는 본인 몫 하위업체(같은 이름)가 가입했을 때 — 초대 메뉴 숨김과 같은 판정(서버 issueForAccount).
  var joinedBadge = <span data-badge="joined" style={badge('#dcfce7','#15803d')}>가입</span>;
  var selfJoined = function(g){ return g.units.some(function(u){ return u.name === g.name && u.companyId; }); };
  var iconBtn = isMobile
    ? {border:'none',background:'none',cursor:'pointer',fontSize:18,color:'#94a3b8',padding:0,minWidth:40,minHeight:40,lineHeight:'20px'}
    : {border:'none',background:'none',cursor:'pointer',fontSize:13,color:'#94a3b8',padding:'2px 5px'};
  var pillSt = {display:'inline-flex',alignItems:'center',gap:4,border:'1px solid #60a5fa',color:'#2563EB',background:'#fff',borderRadius:999,padding:isMobile?'8px 12px':'2px 10px',fontSize:isMobile?12:11,fontWeight:700,cursor:'pointer',marginLeft:8,minHeight:isMobile?40:undefined,boxSizing:'border-box'};
  // 그룹 펼침 caret — 대행사 행 왼쪽 끝. 이 버튼만 펼침/접기를 한다 (행의 나머지는 편집 패널).
  var GROUP_PAGE = 5;               // 대행사 하위 최초 노출 수 ([더 보기] 1회로 전부 펼침)
  var CARET = 40;                   // 최소 터치 타깃
  var GROUP_PAD_L = 8;
  var RAIL_X = GROUP_PAD_L + CARET/2 - 1;  // 하위 행 세로 레일 x — caret 중심에 맞춘다
  // 하위 행 들여쓰기. 데스크탑은 레일+15(=42) 그대로.
  // 모바일은 **대행사 이름 축**(pad 8 + caret 40 + caret marginRight 6 = 54)에 맞춘다 — 42 로 두면
  // 390 실측에서 이름·메타·배지가 레일(27~29)에서 13px 밖에 안 떨어지고, 위 행 caret(8~48)의
  // 오른쪽 끝보다 안쪽에서 시작해 상위 이름(54)보다 12px 왼쪽에 놓인다 → 레일에 겹쳐 보인다(Jin 실기).
  var CHILD_PAD_L = isMobile ? (GROUP_PAD_L + CARET + 6) : (RAIL_X + 15);
  // 글리프가 작아 "펼칠 수 있다"는 인식이 약했다(Jin 8/24) → 크게·채워서 버튼답게 + 열림 시 색 반전.
  // 방향 표시는 글리프 교체 대신 회전으로 — 상태 변화가 눈에 잡힌다.
  var caretBase = {display:'inline-flex',alignItems:'center',justifyContent:'center',width:CARET,minWidth:CARET,height:CARET,
    flexShrink:0,marginRight:6,padding:0,borderRadius:9,fontSize:15,lineHeight:1,cursor:'pointer',
    transition:reduceMotion?'none':'background .12s ease, border-color .12s ease'};
  var caretStyle = function(open, hot){
    return Object.assign({}, caretBase, open
      ? {border:'1px solid #2563EB', background:'#2563EB', color:'#fff'}
      : {border:'1px solid #93c5fd', background: hot ? '#dbeafe' : '#eff6ff', color:'#1d4ed8'});
  };
  var caretGlyphSt = function(open){
    return {display:'block',transform:open?'rotate(90deg)':'none',
      transition:reduceMotion?'none':'transform .16s ease'};
  };
  // 편집이 별도 패널로 빠져 공간이 생겼다 → 입력칸/라벨을 키워 읽고 고치기 편하게 (Jin 8/24)
  var inSt = {border:'1px solid #cbd5e1',borderRadius:8,padding:isMobile?'10px 12px':'9px 11px',fontSize:isMobile?13:13.5,background:'#fff',color:'#1e293b',width:'100%',boxSizing:'border-box',minHeight:isMobile?40:38};
  var lbSt = {fontSize:isMobile?12:11.5,fontWeight:700,color:'#475569'};
  var capSt = {fontSize:isMobile?12:11,color:'#94a3b8',lineHeight:1.5};
  var chkSt = isMobile
    ? {display:'flex',alignItems:'center',gap:8,fontSize:13,minHeight:40,whiteSpace:'nowrap'}
    : {display:'flex',alignItems:'center',gap:6,fontSize:12.5,paddingTop:16,whiteSpace:'nowrap'};
  // 모바일 폼 = 세로 1열 스택 (편집 시트·추가 모달 공통)
  var frmSt = isMobile ? {display:'flex',flexDirection:'column',gap:10} : {display:'flex',gap:10,flexWrap:'wrap'};
  var btnSt = isMobile ? {minHeight:40,padding:'0 14px',fontSize:13} : undefined;
  // fixed 포지션 — 표 래퍼 overflow:hidden/가로스크롤에 잘리지 않게 클릭 좌표 기준으로 띄운다 (menuFor.x/y)
  var menuSt = {position:'fixed',zIndex:300,background:'#fff',border:'1px solid #e2e8f0',borderRadius:8,boxShadow:'0 8px 24px rgba(0,0,0,0.14)',minWidth:isMobile?0:220,maxWidth:isMobile?'calc(100vw - 16px)':undefined,overflow:'hidden',textAlign:'left',fontSize:isMobile?13:12.5};
  var menuItem = {padding:isMobile?'12px 14px':'8px 12px',borderBottom:'1px solid #f1f5f9',cursor:'pointer',whiteSpace:'nowrap'};
  var modalWrap = {position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:16};
  var modalBox = {background:'#fff',borderRadius:14,padding:isMobile?16:20,width:520,maxWidth:isMobile?'94vw':'100%',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 12px 40px rgba(0,0,0,0.18)'};

  // 정렬 가능한 헤더 — 클릭 순환: 오름차순 → 내림차순 → 기본(해제).
  // 기본 정렬(활성 우선 + 이름)에는 의미가 있어 3번째 클릭으로 되돌아올 수 있어야 한다.
  var toggleSort = function(key){
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc');
  };
  var sortTh = function(label, key, right){
    var on = sortKey === key;
    var st = Object.assign({}, TH, right ? {textAlign:'right'} : null,
      {cursor:'pointer', userSelect:'none', color: on ? '#1d4ed8' : TH.color, fontWeight: on ? 800 : TH.fontWeight});
    return (<th key={key} style={st} scope="col"
      aria-sort={on ? (sortDir==='asc'?'ascending':'descending') : 'none'}
      title={on && sortDir==='desc' ? '클릭하면 기본 정렬로' : '클릭해 정렬'}
      onClick={function(){ toggleSort(key); }}>
      {label}<span style={{marginLeft:4,fontSize:isMobile?11:10,color:on?'#1d4ed8':'#cbd5e1'}}>
        {on ? (sortDir==='asc'?'▲':'▼') : '↕'}</span>
    </th>);
  };

  var fld = function(label, node, width, caption){
    var box = isMobile
      ? {width:'100%'}
      : {width:width||undefined, flex:width?undefined:1, minWidth:width?undefined:140};
    return (<div style={Object.assign({display:'flex',flexDirection:'column',gap:3}, box)}>
      <label style={lbSt}>{label}</label>
      {node}
      {caption ? <small style={capSt}>{caption}</small> : null}
    </div>);
  };
  var textIn = function(val, on, ph, right){
    return <input value={val} onChange={function(e){on(e.target.value)}} placeholder={ph||''} style={Object.assign({},inSt,right?{textAlign:'right'}:null)} />;
  };
  var balCell = function(bal, extra){
    var v = parseInt(bal,10)||0;
    return (<span style={{color:balColor(v),fontWeight:v?700:400,whiteSpace:isMobile?'nowrap':undefined}}>{won(v)}
      {extra && v < 0 ? <span style={{fontSize:isMobile?12:10.5,color:'#94a3b8',marginLeft:4}}>선수금</span> : null}
      {extra && v === 0 ? <span style={{fontSize:isMobile?12:10.5,color:'#059669',marginLeft:4}}>완납</span> : null}
    </span>);
  };

  // 모바일에서 감춘 3열을 이름 아래 한 줄로. 발행 대상인데 번호가 없으면 여기서도 경고 톤 유지.
  var unitMeta = function(r){
    return (<span style={metaSt}>
      {r.bizNo ? hi(r.bizNo)
        : <span style={needBiz(r)?{color:'#b45309',fontWeight:700}:null}>사업자번호 미입력</span>}
      {metaDot}{r.companyName || '미연결'}
      {metaDot}{r.serviceFee ? ('수수료 '+fmt(r.serviceFee)) : '수수료 기본'}
    </span>);
  };

  // 삭제 가부 — 잔액이 남아 있으면 원장 전기가 있다는 뜻(클라 선판단). 잔액 0 이어도 서버가 409 로 최종 판정.
  var deletableUnit = function(r){ return (parseInt(r.balance,10)||0) === 0; };
  var deleteReasonUnit = function(r){ return '미수 잔액 ' + won(r.balance) + ' 이 남아 있어 삭제할 수 없습니다 — 비활성으로 전환하세요'; };
  var deletableAcc = function(g){ return g.units.length === 0 && (parseInt(g.balance,10)||0) === 0; };
  var deleteReasonAcc = function(g){
    return g.units.length ? ('하위 ' + g.units.length + '곳이 있어 불가') : ('잔액 ' + won(g.balance) + ' 남아 불가');
  };

  // ── 사업자등록증 OCR (추가 모달) ────────────────────────────────────────
  var ocrBizLicense = function(file){
    if(!file) return;
    setOcrLoading(true);
    var fd = new FormData(); fd.append('image', file);
    authFetch(API+'/admin/companies/ocr-biz-license',{ method:'POST', body:fd })
      .then(function(r){return r.json()}).then(function(d){
        setOcrLoading(false);
        if(d && d.error){ showToast(d.error,'err'); return; }
        // 응답 구조: { success:true, data:{ name, bizNo, ... } } — 결과는 .data 아래 중첩
        var info = (d && d.data) ? d.data : (d || {});
        var got = false;
        if(info.name){ setA('name', info.name); got = true; }
        if(info.bizNo){ setA('bizNo', info.bizNo); got = true; }
        showToast(got ? '사업자등록증 인식 완료' : '인식했으나 상호/사업자번호를 못 읽었습니다 — 수동 입력', got ? undefined : 'err');
      }).catch(function(){ setOcrLoading(false); showToast('인식 실패','err'); });
  };

  // ── 변이 공통 ────────────────────────────────────────────────────────────
  var send = function(url, method, body, okMsg, after){
    var opt = { method: method };
    if(body){ opt.headers = {'Content-Type':'application/json'}; opt.body = JSON.stringify(body); }
    authFetch(url, opt).then(function(r){ return r.json().catch(function(){ return {}; }); }).then(function(d){
      if(d && d.success){ showToast(okMsg); closeAll(); load(); if(after) after(d); return; }
      var msg = (d && d.error) || '처리 실패';
      if(d && d.code === 'HAS_LEDGER') msg = msg + ' — 비활성으로 전환하세요';
      showToast(msg, 'err');
    }).catch(function(){ showToast('처리 실패','err'); });
  };

  // ── 행 편집 ──────────────────────────────────────────────────────────────
  var startEditUnit = function(r){
    setMenuFor(null); setAdd(null);
    setEditing({type:'unit', id:r.id});
    setForm({ name:r.name, bizNo:r.bizNo||'', addr:String(r.addr||''), companyId:r.companyId?String(r.companyId):'',
      serviceFee:String(r.serviceFee||''), wonbuFee:String(r.wonbuFee||''), reissueFee:String(r.reissueFee||''),
      openingBalance:String(r.openingBalance||0), issueTaxInvoice:r.issueTaxInvoice, active:r.active, memo:r.memo||'' });
  };
  var startEditAccount = function(g){
    setMenuFor(null); setAdd(null);
    setEditing({type:'account', id:g.id});
    // 계정 메모·계정레벨 이월은 목록 응답에 없다 — 빈칸으로 두고 입력 시에만 전송(기존값 보존).
    setForm({ name:g.name, bizNo:g.bizNo||'', openingBalance:'', active:g.active, memo:'' });
  };
  var saveUnit = function(r){
    if(!form.name || !form.name.trim()){ showToast('거래처명을 입력하세요','err'); return; }
    send(API+'/agency-clients/'+r.id, 'PUT', scopeBody({
      name: form.name.trim(), bizNo: (form.bizNo||'').trim(),
      addr: (form.addr||'').trim(),
      companyId: form.companyId ? parseInt(form.companyId,10) : 0,
      serviceFee: parseInt(form.serviceFee,10)||0,
      wonbuFee: parseInt(form.wonbuFee,10)||0,
      reissueFee: parseInt(form.reissueFee,10)||0,
      issueTaxInvoice: form.issueTaxInvoice?1:0,
      active: form.active?1:0,
      memo: form.memo||'',
      openingBalance: parseInt(form.openingBalance,10)||0
    }), '수정 완료');
  };
  var saveAccount = function(g){
    if(!form.name || !form.name.trim()){ showToast('대행사명을 입력하세요','err'); return; }
    var body = scopeBody({ name: form.name.trim(), bizNo: (form.bizNo||'').trim(), active: form.active?1:0 });
    if((form.memo||'').trim()) body.memo = form.memo.trim();
    if(String(form.openingBalance||'').trim() !== '') body.openingBalance = parseInt(form.openingBalance,10)||0;
    send(API+'/billing-accounts/'+g.id, 'PUT', body, '수정 완료');
  };
  var setUnitActive = function(r, active){
    send(API+'/agency-clients/'+r.id, 'PUT', scopeBody({active: active?1:0}), active?'다시 활성화했습니다':'비활성으로 전환했습니다');
  };
  var setAccountActive = function(g, active){
    send(API+'/billing-accounts/'+g.id, 'PUT', scopeBody({active: active?1:0}), active?'다시 활성화했습니다':'비활성으로 전환했습니다');
  };
  var removeUnit = function(r){
    if(!window.confirm('거래처 "'+r.name+'" 을(를) 삭제할까요? 되돌릴 수 없습니다.')) return;
    send(API+'/agency-clients/'+r.id+qs(), 'DELETE', null, '삭제 완료');
  };
  var removeAccount = function(g){
    if(!window.confirm('대행사 "'+g.name+'" 을(를) 삭제할까요? 되돌릴 수 없습니다.')) return;
    send(API+'/billing-accounts/'+g.id+qs(), 'DELETE', null, '삭제 완료');
  };

  // ── 소속 이동 / 승격 ────────────────────────────────────────────────────
  var openMove = function(r, val){
    var to = parseInt(val,10)||0;
    var cur = r.isChild ? r.accountId : 0;
    if(to === cur) return;
    setMenuFor(null);
    setMove({ unitId:r.id, name:r.name, toAccountId:to,
      toName: to ? (brokerOptions.filter(function(o){return o.id===to})[0]||{}).name : '',
      balance: parseInt(r.balance,10)||0, carry:false });
  };
  var doMove = function(){
    send(API+'/work-units/'+move.unitId, 'PUT', scopeBody({ accountId: move.toAccountId, carryBalance: !!move.carry }),
      move.toAccountId ? '서류대행을 변경했습니다' : '직접 관리 거래처로 전환했습니다', function(){ setMove(null); });
  };
  var doPromote = function(){
    send(API+'/billing-accounts/'+promote.accountId+'/promote', 'POST', scopeBody({}), '대행사로 승격했습니다', function(){ setPromote(null); });
  };

  // ── + 거래처 추가 ────────────────────────────────────────────────────────
  var startAdd = function(){
    closeAll();
    // issueTaxInvoice 기본 1 = 서버 createClient 의 미지정 default 와 동일 (기존 동작 보존).
    setAdd({ type:'direct', name:'', bizNo:'', addr:'', companyId:'', serviceFee:'', wonbuFee:'', reissueFee:'',
      issueTaxInvoice:1, openingBalance:'0', memo:'', accountId:'' });
  };
  var submitAdd = function(){
    if(!add.name || !add.name.trim()){ showToast(add.type==='broker'?'대행사명을 입력하세요':'거래처명을 입력하세요','err'); return; }
    if(add.type === 'sub' && !add.accountId){ showToast('서류대행 업체를 선택하세요','err'); return; }
    if(add.type === 'broker'){
      send(API+'/billing-accounts', 'POST', scopeBody({
        kind:'broker', name: add.name.trim(), bizNo:(add.bizNo||'').trim(), memo: add.memo||'',
        openingBalance: parseInt(add.openingBalance,10)||0
      }), '대행사 추가 완료', function(){ setAdd(null); });
      return;
    }
    var body = scopeBody({
      name: add.name.trim(), bizNo: (add.bizNo||'').trim(),
      addr: (add.addr||'').trim(),
      companyId: add.companyId ? parseInt(add.companyId,10) : 0,
      serviceFee: parseInt(add.serviceFee,10)||0,
      wonbuFee: parseInt(add.wonbuFee,10)||0,
      reissueFee: parseInt(add.reissueFee,10)||0,
      issueTaxInvoice: add.issueTaxInvoice?1:0, memo: add.memo||'',
      openingBalance: parseInt(add.openingBalance,10)||0
    });
    if(add.type === 'sub') body.accountId = parseInt(add.accountId,10)||0;
    send(API+'/agency-clients', 'POST', body, '거래처 추가 완료', function(){ setAdd(null); });
  };

  // ── 행 렌더 ──────────────────────────────────────────────────────────────
  var menuBox = function(items){
    // 뷰포트 밖으로 나가지 않게 상/좌 모두 클램프 (모바일 폭 220 미만 대비 — 우측 잘림 방지)
    var vw = (typeof window !== 'undefined' && window.innerWidth) || 1024;
    var vh = (typeof window !== 'undefined' && window.innerHeight) || 800;
    var pos;
    if(menuFor && menuFor.y !== undefined){
      var left = Math.max(8, (menuFor.x||0) - 220);
      left = Math.min(left, Math.max(8, vw - 228));
      pos = {top: Math.max(8, Math.min(menuFor.y, vh - 44*items.length - 16)), left: left};
    } else {
      pos = {top: 80, right: 24};
    }
    return (<div className="ca-menu" style={Object.assign({},menuSt,pos)} onClick={function(e){e.stopPropagation()}}>{items.map(function(it,i){
      return (<div key={i} style={Object.assign({},menuItem,it.disabled?{color:'#94a3b8',cursor:'default'}:(it.danger?{color:'#dc2626',fontWeight:700}:null))}
        onClick={function(){ if(it.disabled) return; setMenuFor(null); it.on(); }}>
        {it.label}{it.note ? <span style={{color:'#94a3b8',fontWeight:400}}> — {it.note}</span> : null}
      </div>);
    })}</div>);
  };

  // 편집 폼 본문 — 데스크탑은 오른쪽 슬라이드 패널 안에, 모바일은 풀스크린 시트 안에 같은 노드를 넣는다.
  // (옛 데스크탑 인라인 행(td colSpan)은 폐지 — 열 때마다 목록이 아래로 밀려 위치를 잃었다. Jin 8/24)
  var unitEditFields = function(r){
    return (<React.Fragment>
          {sec('기본 정보')}
          <div style={frmSt}>
            {fld('거래처명', textIn(form.name, function(v){setF('name',v)}, '거래처명'), 210)}
            {fld('사업자번호', textIn(form.bizNo, function(v){setF('bizNo',v)}, '000-00-00000'), 210, '정산서·계산서에 실립니다')}
            {fld('연결 회사 (ERP)', (
              <select value={form.companyId} onChange={function(e){setF('companyId',e.target.value)}} style={inSt}>
                <option value="">(미연결)</option>
                {companyOptions(r.companyId, r.companyName).map(function(o){return <option key={o.id} value={o.id}>{o.name}</option>})}
              </select>
            ), 210)}
            {fld('서류대행', (
              <select value={r.isChild ? String(r.accountId) : '0'} onChange={function(e){ openMove(r, e.target.value); }} style={inSt}>
                <option value="0">(직거래)</option>
                {brokerOptions.map(function(o){return <option key={o.id} value={o.id}>{o.name}</option>})}
              </select>
            ), 210, '다른 대행사 또는 (직거래) 선택 = 독립')}
            {fld('주소', textIn(form.addr, function(v){setF('addr',v)}, '예: 인천 중구 …'), 440, '말소신청서 위임자란에 실립니다')}
          </div>
          {sec('정산 요율')}
          <div style={frmSt}>
            {/* 210 × 2열 — 140 3개면 이월미수가 혼자 다음 줄로 밀려 한 줄이 비었다 (브라우저 확인) */}
            {fld('수수료', textIn(form.serviceFee, function(v){setF('serviceFee',v)}, '0', true), 210)}
            {fld('원부', textIn(form.wonbuFee, function(v){setF('wonbuFee',v)}, '0', true), 210)}
            {fld('재교부', textIn(form.reissueFee, function(v){setF('reissueFee',v)}, '0', true), 210, '0 = 대행사 기본 재교부료')}
            {fld('이월 미수 잔액', textIn(form.openingBalance, function(v){setF('openingBalance',v)}, '0', true), 210, '과거 장부에서 넘어온 미수')}
          </div>
          <div style={{display:'flex',gap:18,flexWrap:'wrap',marginTop:12}}>
            <div style={{display:'flex',flexDirection:'column',gap:3}}>
              <label style={chkSt}><input type="checkbox" checked={!!form.issueTaxInvoice} onChange={function(e){setF('issueTaxInvoice',e.target.checked?1:0)}} /> 세금계산서 발행 (기본값)</label>
              <small style={capSt}>달마다 다르게 하려면 정산 › 장부 머리말에서 그 달만 바꿀 수 있습니다</small>
            </div>
            <label style={chkSt}><input type="checkbox" checked={!!form.active} onChange={function(e){setF('active',e.target.checked?1:0)}} /> 활성</label>
          </div>
          {sec('기타')}
          <div style={frmSt}>{fld('메모', textIn(form.memo, function(v){setF('memo',v)}, ''))}</div>
    </React.Fragment>);
  };

  // 액션은 패널 하단 고정 바로 — 필드가 길어져도 저장 버튼을 찾아 스크롤할 일이 없다.
  var unitEditActions = function(r){
    return (<React.Fragment>
      <button className="btn btn-blue btn-sm" style={btnSt} onClick={function(){saveUnit(r)}}>저장</button>
      <button className="btn btn-outline btn-sm" style={btnSt} onClick={closeAll}>취소</button>
      <div style={{flex:1}} />
      {!deletableUnit(r) && <span style={{fontSize:isMobile?12:11.5,color:'#94a3b8',flexBasis:isMobile?'100%':undefined}}>{deleteReasonUnit(r)}</span>}
      <button className="btn btn-outline btn-sm" disabled={!deletableUnit(r)}
        style={Object.assign({color:'#dc2626',opacity:deletableUnit(r)?1:0.45}, btnSt)}
        onClick={function(){ if(deletableUnit(r)) removeUnit(r); }}>삭제</button>
    </React.Fragment>);
  };

  var accountEditFields = function(g){
    return (<React.Fragment>
          {sec('기본 정보')}
          <div style={frmSt}>
            {fld('대행사명', textIn(form.name, function(v){setF('name',v)}, '대행사명'), 210)}
            {fld('사업자번호', textIn(form.bizNo, function(v){setF('bizNo',v)}, '000-00-00000'), 210)}
          </div>
          {sec('정산')}
          <div style={frmSt}>
            {fld('이월 미수 잔액 (계정)', textIn(form.openingBalance, function(v){setF('openingBalance',v)}, '입력 시에만 반영', true), 210, '하위업체 개별 이월은 각 거래처에서')}
          </div>
          <div style={{marginTop:12}}>
            <label style={chkSt}><input type="checkbox" checked={!!form.active} onChange={function(e){setF('active',e.target.checked?1:0)}} /> 활성</label>
          </div>
          <div style={{fontSize:isMobile?12:12,color:'#1d4ed8',background:'#eff6ff',border:'1px solid #bfdbfe',
            borderRadius:8,padding:'10px 12px',marginTop:12,lineHeight:1.6}}>
            하위 업체 {g.units.length}곳의 이름·수수료·사업자번호는 <b>목록에서 그 업체를 눌러</b> 수정합니다.
            여기서 <b>활성</b>을 끄면 {g.name}와(과) 하위 {g.units.length}곳 모두 신규 청구가 중단됩니다.
          </div>
          {sec('기타')}
          <div style={frmSt}>{fld('메모', textIn(form.memo, function(v){setF('memo',v)}, '입력 시에만 반영'))}</div>
    </React.Fragment>);
  };

  var accountEditActions = function(g){
    return (<React.Fragment>
      <button className="btn btn-blue btn-sm" style={btnSt} onClick={function(){saveAccount(g)}}>저장</button>
      <button className="btn btn-outline btn-sm" style={btnSt} onClick={closeAll}>취소</button>
    </React.Fragment>);
  };

  // 데스크탑 편집 패널 — 화면 오른쪽에서 밀려나오는 슬라이드 패널 (목록은 밀리지 않는다).
  // 닫기 3경로: ✕ 버튼 · ESC(위 useEffect) · 배경 클릭. 폼 본문은 시트와 동일 노드 재사용.
  // 별도 영역이 된 만큼 넉넉하게 — 380 은 2열 배치가 눌려 답답했다(Jin 8/24).
  // 520px 면 라벨/입력이 제 크기를 찾고 2열이 자연스럽다. 액션은 하단 고정 바로 빼 스크롤과 무관하게 닿는다.
  var editPanel = function(title, sub, body, actions){
    return (<div style={{position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:9998}}>
      <div data-testid="ca-panel-backdrop" onClick={closeAll}
        style={{position:'absolute',top:0,left:0,right:0,bottom:0,background:'rgba(15,23,42,0.28)',
          opacity:panelIn?1:0,transition:reduceMotion?'none':'opacity .22s ease'}} />
      <div role="dialog" aria-label={title}
        style={{position:'absolute',top:0,right:0,bottom:0,width:'min(520px, 94vw)',background:'#fff',
          boxShadow:'-8px 0 32px rgba(0,0,0,0.16)',display:'flex',flexDirection:'column',
          transform:panelIn?'translateX(0)':'translateX(100%)',transition:reduceMotion?'none':'transform .22s ease'}}>
        <div style={{display:'flex',alignItems:'flex-start',gap:10,padding:'18px 22px 16px',borderBottom:'1px solid #e2e8f0'}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:19,fontWeight:800,color:'#1B3A5C',letterSpacing:'-0.02em',
              overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{title}</div>
            {sub ? <div style={{fontSize:12.5,color:'#64748b',marginTop:4,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>{sub}</div> : null}
          </div>
          <button onClick={closeAll} title="닫기" aria-label="편집 패널 닫기"
            style={{border:'1px solid #e2e8f0',borderRadius:8,background:'#fff',fontSize:16,color:'#64748b',
              minWidth:36,minHeight:36,cursor:'pointer',padding:0,flexShrink:0}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'6px 22px 22px'}}>{body}</div>
        {actions ? <div style={{borderTop:'1px solid #e2e8f0',padding:'12px 22px',background:'#f8fafc',
          display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>{actions}</div> : null}
      </div>
    </div>);
  };

  // 패널 안 섹션 구분 — 필드가 많아 한 덩어리로 두면 훑기 어렵다. 라벨 + 가로선.
  var secSt = {display:'flex',alignItems:'center',gap:8,margin:isMobile?'16px 0 8px':'20px 0 10px'};
  var secTx = {fontSize:12,fontWeight:800,color:'#1B3A5C',whiteSpace:'nowrap',letterSpacing:'-0.01em'};
  var sec = function(t){ return (<div style={secSt}><span style={secTx}>{t}</span>
    <div style={{flex:1,height:1,background:'#e2e8f0'}} /></div>); };

  // 모바일 편집 시트 — 표(가로 스크롤) 밖 fixed 오버레이. 폼은 데스크탑과 동일 노드, 세로 1열 스택.
  var editSheet = function(title, sub, body, actions){
    return (<div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.45)',zIndex:9998,display:'flex',flexDirection:'column',justifyContent:'flex-end'}}
      onClick={closeAll}>
      <div onClick={function(e){e.stopPropagation()}}
        style={{background:'#fff',borderTopLeftRadius:16,borderTopRightRadius:16,maxHeight:'92vh',overflowY:'auto',WebkitOverflowScrolling:'touch',padding:'14px 14px 28px',boxShadow:'0 -8px 32px rgba(0,0,0,0.2)'}}>
        <div style={{display:'flex',alignItems:'flex-start',gap:8,marginBottom:12}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:15,fontWeight:800,color:'#1B3A5C',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{title}</div>
            {sub ? <div style={{fontSize:12,color:'#94a3b8',marginTop:2}}>{sub}</div> : null}
          </div>
          <button onClick={closeAll} style={{border:'none',background:'none',fontSize:18,color:'#94a3b8',minWidth:40,minHeight:40,cursor:'pointer',padding:0}}>✕</button>
        </div>
        <div>{body}</div>
        {actions ? <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',
          marginTop:16,paddingTop:14,borderTop:'1px solid #e2e8f0'}}>{actions}</div> : null}
      </div>
    </div>);
  };

  var unitRow = function(r){
    var editingThis = editing && editing.type==='unit' && editing.id===r.id;
    var menuThis = menuFor && menuFor.type==='unit' && menuFor.id===r.id;
    // 편집 패널이 열려 있는 동안 그 행을 선택 상태로 — 패널이 어느 행의 것인지 목록에서 보여야 한다
    var rowBg = editingThis ? '#dbeafe' : (r.isChild ? (r.active?'#f5f9ff':'#f8fafc') : (r.active?'#fff':'#f8fafc'));
    var tdSt = Object.assign({}, TD, {background:rowBg, borderBottom: r.isChild?'1px solid #e4eefb':'1px solid #f1f5f9', opacity: r.active?1:0.7});
    // 하위 행 = 들여쓰기 + 세로 레일. 레일은 셀 배경 그라디언트로 그려 행 사이가 끊기지 않는다
    // (자식 노드로 그리면 td 세로 패딩만큼 끊긴다). background 축약형과 충돌하지 않게 명시 분해.
    var nameTd = Object.assign({}, tdSt, {paddingLeft: r.isChild?CHILD_PAD_L:12}, nameWrap);
    if(r.isChild){
      delete nameTd.background;
      nameTd.backgroundColor = rowBg;
      nameTd.backgroundImage = 'linear-gradient(#93c5fd,#93c5fd)';
      nameTd.backgroundRepeat = 'no-repeat';
      nameTd.backgroundSize = '2px 100%';
      nameTd.backgroundPosition = RAIL_X + 'px 0';
    }
    if(editingThis) nameTd.boxShadow = 'inset 3px 0 0 #2563EB';
    // 행 클릭 = 편집 토글 (Jin 8/13 — 별도 버튼 제거). 버튼·메뉴 클릭은 편집 진입 제외
    return (<tr key={'u'+r.id} style={{cursor:'pointer'}} title={editingThis?undefined:'클릭해 수정'}
        onClick={function(e){ if(e.target.closest && e.target.closest('button,.ca-menu')) return; editingThis?closeAll():startEditUnit(r); }}>
        <td style={nameTd}>
          <span style={{color: r.active?'#1e293b':'#64748b'}}>{hi(r.name)}</span>
          {!r.active && <span style={badge('#e2e8f0','#64748b')}>비활성</span>}
          {r.companyId > 0 && joinedBadge}
          {/* 소속 꼬리표 — 모바일은 들여쓰기·레일이 이미 보여주므로 메타 줄로 넘기지 않고 생략 */}
          {r.isChild && !isMobile && <span style={{fontSize:11,color:'#94a3b8',marginLeft:6}}>· 서류대행: {r.accountName}</span>}
          {isMobile && unitMeta(r)}
        </td>
        {/* 발행 대상인데 번호가 없으면 계산서가 막힌다 → 단순 '미입력' 대신 경고 톤 */}
        {!isMobile && <td style={tdSt}>{r.bizNo ? hi(r.bizNo)
          : (needBiz(r) ? <span style={{color:'#b45309',fontWeight:700}} title="세금계산서 발행 대상 — 사업자번호가 있어야 발행됩니다">미입력</span>
                        : <span style={HINT}>미입력</span>)}</td>}
        {!isMobile && <td style={tdSt}>{r.companyName || <span style={HINT}>미연결</span>}</td>}
        {!isMobile && <td style={Object.assign({},tdSt,{textAlign:'right'})}>{r.serviceFee ? fmt(r.serviceFee) : <span style={HINT}>-</span>}</td>}
        <td style={Object.assign({},tdSt,{textAlign:'right'})}>{balCell(r.balance, true)}</td>
        <td style={Object.assign({},tdSt,{textAlign:'right',whiteSpace:'nowrap',position:'relative'})}>
          {/* 모바일 관리 칸은 ⋯ 하나만 — '다시 활성' 을 같이 두면 이 칸의 min-content(138px)가
              거래처 이름 칸(112px)보다 넓어진다(실측). 같은 동작은 ⋯ 메뉴에 그대로 있다. */}
          {!r.active && !isMobile && <button className="btn btn-outline btn-sm" style={btnSt} onClick={function(){setUnitActive(r,true)}}>다시 활성</button>}
          <button style={iconBtn} title="더보기" onClick={function(e){ var rc=e.currentTarget.getBoundingClientRect(); setMenuFor(menuThis?null:{type:'unit',id:r.id,x:rc.right,y:rc.bottom+4}); }}>⋯</button>
          {menuThis && menuBox([
            {label:'장부 보기', on:function(){ navigate('/main/ca-settlement'); }},
            r.active
              ? {label:'비활성으로 전환', on:function(){ setUnitActive(r,false); }}
              : {label:'다시 활성', on:function(){ setUnitActive(r,true); }}
          ].concat(r.isChild ? [] : [{label:'대행사로 승격', on:function(){ setPromote({accountId:r.accountId, name:r.name, balance:r.balance}); }}])
           .concat(!r.companyId && r.active ? [{label:'앱 초대 링크 보내기', on:function(){ setInvite({id:r.id, name:r.name}); }}] : [])
           .concat([{label:'삭제', danger:true, disabled:!deletableUnit(r),
              note: deletableUnit(r) ? '' : '잔액 ' + won(r.balance) + ' 남음',
              on:function(){ removeUnit(r); }}]))}
        </td>
      </tr>);
  };

  // 그룹 끝 푸터 — 264행 내려간 뒤 맨 위로 되돌아가지 않고 그 자리에서 접는다.
  // 검색/필터 중엔 isOpen 이 강제 true 라 접기가 무의미 → 끝 표시만 남긴다.
  var groupFooterRow = function(x){
    var g = x.g;
    var forced = !!(ql || bizFilterOn);
    var total = x.units.length;
    var shown = Math.min(total, shownLimit(g.id));
    var rest = total - shown;
    var tdSt = Object.assign({}, TD, {background:'#f8fafc', borderBottom:'1px solid #e2e8f0', paddingLeft:RAIL_X});
    var muted = {fontSize:isMobile?12:11.5,color:'#64748b'};
    return (<tr key={'gf'+g.id}>
      <td colSpan={COLS} style={tdSt}>
        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <span style={{display:'inline-block',width:2,height:14,background:'#93c5fd',flexShrink:0}} />
          {rest > 0 ? (<React.Fragment>
            {/* 남은 게 있을 때가 이 화면의 기본 상태 — 버튼을 먼저 두고 숫자를 뒤에 붙인다 */}
            <button onClick={function(){showMore(g.id, total)}}
              aria-label={g.name+' 하위 거래처 '+rest+'곳 모두 보기'}
              style={Object.assign({},pillSt,{marginLeft:0,background:'#2563EB',color:'#fff',border:'1px solid #2563EB',fontWeight:700})}>
              ▾ 전체 보기</button>
            <span style={muted}>{shown}/{total}곳 · {rest}곳 남음</span>
          </React.Fragment>) : (
            <span style={Object.assign({},muted,{fontWeight:700})}>{g.name} {total}곳 끝</span>
          )}
          {!forced && <button onClick={function(){collapseGroup(g.id)}} aria-label={g.name+' 하위 거래처 접기'}
            style={Object.assign({},pillSt,{marginLeft:rest>0?'auto':0})}>▴ 접기</button>}
        </div>
      </td>
    </tr>);
  };

  var groupRows = function(x){
    var g = x.g;
    var editingThis = editing && editing.type==='account' && editing.id===g.id;
    var menuThis = menuFor && menuFor.type==='account' && menuFor.id===g.id;
    // 그룹 머리글 sticky — 264행을 스크롤하는 동안 지금 어느 그룹인지 계속 보이게.
    // <tr> 에 sticky 는 브라우저가 무시한다 → 셀(td)마다 건다.
    // border-collapse:collapse 에선 stuck 상태의 셀 border 가 따라오지 않는다 → inset boxShadow 로 밑줄.
    // top 은 실측값 — 스크롤 조상은 셸의 .dash-content 이고 padding:24px (App.css) 이다.
    // sticky 의 기준(scrollport)은 그 padding box 라 top:0 이면 24px 띄워 붙고, 그 띠로 하위 행이
    // 비쳐 지나간다 → -24 로 컨테이너 상단(헤더 바로 아래)에 딱 붙인다.
    // 모바일은 가로 스크롤 래퍼(hScroll)가 스크롤 조상이고 세로로는 스크롤되지 않아 영향 0.
    var stick = {position:'sticky', top:STICKY_TOP + Math.floor(theadH) - 1, zIndex:2, boxShadow:'inset 0 -1px 0 #cbd5e1'};
    var tdSt = Object.assign({}, TD, {background: editingThis?'#dbeafe':'#eff6ff', fontWeight:600, borderBottom:'1px solid #e2e8f0', opacity: g.active?1:0.7}, stick);
    var open = isOpen(g.id);
    return (<React.Fragment key={'g'+g.id}>
      <tr style={{cursor:'pointer'}} title={editingThis?undefined:'클릭해 대행사 정보 수정'}
        onClick={function(e){ if(e.target.closest && e.target.closest('button,.ca-menu')) return; editingThis?closeAll():startEditAccount(g); }}>
        <td style={Object.assign({},tdSt,{paddingLeft:GROUP_PAD_L},nameWrap,editingThis?{boxShadow:'inset 3px 0 0 #2563EB, inset 0 -1px 0 #cbd5e1'}:null)}>
          <div style={{display:'flex',alignItems:'center',minWidth:0}}>
            {/* 펼침 전용 — 행 클릭(편집)과 동작을 분리한다. 나머지 행 영역은 그대로 편집 진입 */}
            {g.units.length > 0
              ? (<button style={caretStyle(open, hotCaret===g.id)} aria-expanded={open}
                  aria-label={g.name+' 하위 '+g.units.length+'곳 '+(open?'접기':'펼치기')}
                  title={open?'하위 거래처 접기':'하위 거래처 펼치기'}
                  onMouseEnter={function(){setHotCaret(g.id)}} onMouseLeave={function(){setHotCaret(null)}}
                  onClick={function(e){ e.stopPropagation(); toggleGroup(g.id); }}>
                  <span style={caretGlyphSt(open)}>▶</span></button>)
              : <span style={{display:'inline-block',width:CARET,marginRight:6,flexShrink:0}} />}
            <span style={{minWidth:0}}>
              <span style={{color:'#1B3A5C',fontWeight:800}}>{hi(g.name)}</span>
              <span style={badge('#2563EB','#fff')}>대행사</span>
              {!g.active && <span style={badge('#e2e8f0','#64748b')}>비활성</span>}
              {selfJoined(g) && joinedBadge}
              {g.units.length > 0 && <span style={{fontSize:isMobile?12:11,color:'#64748b',fontWeight:700,marginLeft:8,whiteSpace:'nowrap'}}>{g.units.length}곳</span>}
              {isMobile && <span style={metaSt}>{g.bizNo ? hi(g.bizNo) : '사업자번호 미입력'}</span>}
            </span>
          </div>
        </td>
        {!isMobile && <td style={tdSt}>{g.bizNo ? hi(g.bizNo) : <span style={HINT}>미입력</span>}</td>}
        {!isMobile && <td style={Object.assign({},tdSt,HINT)}>—</td>}
        {!isMobile && <td style={Object.assign({},tdSt,HINT,{textAlign:'right'})}>—</td>}
        <td style={Object.assign({},tdSt,{textAlign:'right'})}>{balCell(g.balance, false)}</td>
        <td style={Object.assign({},tdSt,{textAlign:'right',whiteSpace:'nowrap'})}>
          {!g.active && !isMobile && <button className="btn btn-outline btn-sm" style={btnSt} onClick={function(){setAccountActive(g,true)}}>다시 활성</button>}
          <button style={iconBtn} title="더보기" onClick={function(e){ var rc=e.currentTarget.getBoundingClientRect(); setMenuFor(menuThis?null:{type:'account',id:g.id,x:rc.right,y:rc.bottom+4}); }}>⋯</button>
          {menuThis && menuBox([
            {label:'장부 보기', on:function(){ navigate('/main/ca-settlement'); }},
            g.active
              ? {label:'비활성으로 전환', on:function(){ setAccountActive(g,false); }}
              : {label:'다시 활성', on:function(){ setAccountActive(g,true); }}
          // 대행업체 초대 (Jin 10/29) — 본인 몫 하위업체(같은 이름)가 아직 가입 안 한 활성 대행업체만
          ].concat(g.active && !selfJoined(g) ? [{label:'앱 초대 링크 보내기', on:function(){ setInvite({accountId:g.id, name:g.name}); }}] : [])
           .concat([
            {label:'삭제', danger:true, disabled:!deletableAcc(g),
              note: deletableAcc(g) ? '' : deleteReasonAcc(g),
              on:function(){ removeAccount(g); }}
          ]))}
        </td>
      </tr>
      {open && x.units.slice(0, shownLimit(g.id)).map(function(u){ return unitRow(u); })}
      {open && x.units.length > 0 && groupFooterRow(x)}
    </React.Fragment>);
  };

  var legacyRow = function(r){
    var tdSt = Object.assign({}, TD, {background:'#f8fafc', color:'#64748b'});
    return (<tr key={'l'+r.id}>
      <td style={tdSt}>{hi(r.name)}<span style={badge('#e2e8f0','#64748b')}>종전 잔액</span>
        {isMobile && r.bizNo && <span style={metaSt}>{r.bizNo}</span>}</td>
      {!isMobile && <td style={tdSt}>{r.bizNo || <span style={HINT}>—</span>}</td>}
      {!isMobile && <td style={Object.assign({},tdSt,HINT)}>—</td>}
      {!isMobile && <td style={Object.assign({},tdSt,HINT,{textAlign:'right'})}>—</td>}
      <td style={Object.assign({},tdSt,{textAlign:'right'})}>{balCell(r.balance, false)}</td>
      <td style={Object.assign({},tdSt,{textAlign:'right'})}><span style={{fontSize:isMobile?12:11.5,color:'#94a3b8',whiteSpace:isMobile?'nowrap':undefined}}>{isMobile?'자동 정리':'완납 시 자동 정리'}</span></td>
    </tr>);
  };

  var systemRow = function(r){
    var tdSt = Object.assign({}, TD, {background:'#fff', color:'#64748b'});
    return (<tr key={'s'+r.id}>
      <td style={tdSt}>{hi(r.name)}<span style={badge('#fef3c7','#b45309','1px solid #b45309')}>시스템</span></td>
      {!isMobile && <td style={Object.assign({},tdSt,HINT)}>—</td>}
      {!isMobile && <td style={Object.assign({},tdSt,HINT)}>—</td>}
      {!isMobile && <td style={Object.assign({},tdSt,HINT,{textAlign:'right'})}>—</td>}
      <td style={Object.assign({},tdSt,{textAlign:'right'})}>{balCell(r.balance, false)}</td>
      <td style={Object.assign({},tdSt,{textAlign:'right'})}>
        <span style={{fontSize:isMobile?12:11.5,color:'#2563EB',cursor:'pointer',whiteSpace:isMobile?'nowrap':undefined,display:isMobile?'inline-block':undefined,minHeight:isMobile?40:undefined,lineHeight:isMobile?'40px':undefined}} onClick={function(){navigate('/main/ca-settlement')}}>{isMobile?'재지정 →':'입금 탭에서 재지정 →'}</span>
      </td>
    </tr>);
  };

  // ── 추가 모달 ────────────────────────────────────────────────────────────
  var typeCard = function(key, icon, title, desc){
    var on = add && add.type === key;
    return (<div onClick={function(){setA('type',key)}}
      style={{border:on?'1.5px solid #2563EB':'1.5px solid #e2e8f0',background:on?'#eff6ff':'#fff',borderRadius:10,padding:'12px 10px',textAlign:'center',cursor:'pointer',fontSize:12,color:'#64748b'}}>
      {icon}<b style={{display:'block',fontSize:13,color:on?'#2563EB':'#1e293b',margin:'4px 0 2px'}}>{title}</b>{desc}
    </div>);
  };

  // ── 도움말 (문단 나열 → 요약 줄 + 2 카드) ────────────────────────────────
  // 작업 유형 탭(CancelAgencyWorkTypes)의 도움말과 같은 문법·같은 색을 쓴다 — 두 화면이 한 세그먼트다.
  var gTitle = {fontSize:12.5,fontWeight:800,color:'#1B3A5C'};
  var gDesc = {fontSize:isMobile?12:11.5,color:'#64748b',lineHeight:1.55};
  var gCard = function(title, body){
    return (<div style={{flex:1,minWidth:0,border:'1px solid #e2e8f0',borderRadius:8,background:'#f8fafc',padding:'10px 12px'}}>
      <div style={Object.assign({marginBottom:3},gTitle)}>{title}</div>
      <div style={gDesc}>{body}</div>
    </div>);
  };
  var guide = (
    <div style={{marginBottom:12}}>
      <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:isMobile?'8px 10px':'8px 12px',
        display:'flex',alignItems:'center',gap:8}}>
        <span style={{flex:1,minWidth:0,fontSize:isMobile?12.5:13,color:'#92400e',lineHeight:1.5}}>
          수수료를 <b>비워 둔 거래처</b>는 대행사 기본 금액으로 정산됩니다.
        </span>
        <button type="button" aria-expanded={guideOpen ? 'true' : 'false'} onClick={function(){setGuideOpen(!guideOpen)}}
          style={{flexShrink:0,border:'1px solid #fcd34d',background:'#fff',color:'#92400e',borderRadius:6,cursor:'pointer',
            fontWeight:700,fontSize:12,padding:isMobile?'0 10px':'5px 10px',minHeight:isMobile?40:undefined,whiteSpace:'nowrap'}}>
          {guideOpen ? '도움말 ▴' : '도움말 ▾'}
        </button>
      </div>
      {guideOpen && (
        <div style={{border:'1px solid #e2e8f0',borderRadius:10,background:'#fff',padding:isMobile?'12px':'14px 16px',marginTop:8}}>
          <div style={{display:'flex',flexDirection:isMobile?'column':'row',gap:8,alignItems:'stretch'}}>
            {gCard('거래처 수수료', <span>거래처에 수수료를 <b>입력해 두면</b> 그 금액으로, <b>비워 두면</b> 대행사 기본 금액으로 정산됩니다.</span>)}
            {/* JSX 는 태그에 인접한 줄바꿈을 지운다 → 문장 사이 공백이 사라짐. {' '} 로 명시. */}
            {gCard('회사 연결', <span>
              회사를 연결해 두면 그 회사의 말소요청이 완료될 때마다 이 거래처 정산에 자동으로 쌓입니다.{' '}
              {linkable.length===0 && <span>연결 후보는 <b>아직 없습니다</b> — 말소요청이 들어오면 생깁니다.</span>}
            </span>)}
          </div>
          {/* '사용 방법' 진입은 헤더 밑줄 탭(사용 방법 ↗) 하나로 — 도움말 안 중복 버튼 제거 (Jin 8/26) */}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* 상단: 대행사 선택(admin) + 검색 + 카운트 + 추가 (모바일 = 검색 1행 100% → 카운트 → 버튼 우측) */}
      <div style={{display:'flex',alignItems:'center',gap:isMobile?8:10,marginBottom:12,flexWrap:'wrap'}}>
        {!isCancelAgency && (
          <select value={agencyId} onChange={function(e){setAgencyId(e.target.value);setNeedBizOnly(false);closeAll();}}
            style={Object.assign({padding:isMobile?'10px 12px':'8px 10px',border:'1px solid #cbd5e1',borderRadius:8}, isMobile?{flex:'1 1 100%',width:'100%',minWidth:0,boxSizing:'border-box',fontSize:13,minHeight:40}:null)}>
            <option value="">대행사 선택…</option>
            {(agencies||[]).filter(function(a){return a.active}).map(function(a){return <option key={a.id} value={a.id}>{a.name}</option>})}
          </select>
        )}
        {canQuery() && <input value={q} onChange={function(e){setQ(e.target.value)}} placeholder="거래처명·사업자번호 검색"
          style={Object.assign({padding:isMobile?'10px 12px':'8px 10px',border:'1px solid #cbd5e1',borderRadius:8,minWidth:isMobile?0:240}, isMobile?{flex:'1 1 100%',width:'100%',boxSizing:'border-box',fontSize:13,minHeight:40}:null)} />}
        {canQuery() && <span style={Object.assign({fontSize:12,color:'#94a3b8'}, isMobile?{flex:'1 1 100%',lineHeight:1.6}:null)}>
          {q.trim() ? <span>검색 <b style={{color:'#475569'}}>{shownCount}</b>건 · </span> : null}
          거래처 <b style={{color:'#475569'}}>{totalUnits}</b>곳 · 대행사 <b style={{color:'#475569'}}>{groups.length}</b> · 직거래 <b style={{color:'#475569'}}>{directs.length}</b>
        </span>}
        {!isMobile && <div style={{flex:1}} />}
        {canQuery() && (isMobile
          ? (<div style={{display:'flex',gap:8,width:'100%',justifyContent:'flex-end'}}>
              {canShare && <button className="btn btn-outline btn-sm" style={btnSt} onClick={function(){ closeAll(); setShareOpen(true); }}>대표 링크</button>}
              <button className="btn btn-blue btn-sm" style={btnSt} onClick={startAdd}>＋ 거래처 추가</button>
              <button className="btn btn-outline btn-sm" style={btnSt} onClick={load}>새로고침</button>
            </div>)
          : (<React.Fragment>
              {canShare && <button className="btn btn-outline btn-sm" onClick={function(){ closeAll(); setShareOpen(true); }}>대표 링크</button>}
              <button className="btn btn-blue btn-sm" onClick={startAdd}>＋ 거래처 추가</button>
              <button className="btn btn-outline btn-sm" onClick={load}>새로고침</button>
            </React.Fragment>))}
      </div>

      {/* 대표 링크로 가입해 오토덱스 승인을 마친 업체 — 연결 확인 대기 · 연결 안 함 (둘 다 0 이면 렌더 없음) */}
      {canShare && <CancelAgencyJoinRequests key={shareScope} scope={shareScope} agencyName={agencyName} pickUnits={joinPickUnits} showToast={showToast} onLinked={load} />}

      {canQuery() && guide}

      {/* 발행 대상 사업자번호 미입력 요약 = 필터 토글. 대상 0 곳이면 렌더 자체가 사라진다. */}
      {canQuery() && needBizCount > 0 && (
        <div style={{marginBottom:12}}>
          <span onClick={function(){setNeedBizOnly(!needBizOnly)}}
            style={{display:'inline-flex',alignItems:'center',gap:6,borderRadius:999,cursor:'pointer',fontWeight:700,boxSizing:'border-box',
              padding:isMobile?'8px 14px':'4px 12px',fontSize:isMobile?12:11.5,minHeight:isMobile?40:undefined,
              border:'1px solid '+(bizFilterOn?'#b45309':'#fde68a'),
              background:bizFilterOn?'#b45309':'#fffbeb',
              color:bizFilterOn?'#fff':'#92400e'}}>
            세금계산서 발행 대상 {needBizCount}곳 사업자번호 미입력{bizFilterOn ? ' — 해제 ✕' : ''}
          </span>
        </div>
      )}

      {!canQuery() && <div style={{color:'#94a3b8',padding:'40px 0',textAlign:'center'}}>대행사를 선택하세요.</div>}

      {/* 카드 overflow 는 hidden 이 아니라 clip — hidden 은 스크롤 컨테이너를 만들어 안쪽 sticky
          그룹 머리글이 이 카드에 갇힌다(페이지 스크롤 .dash-content 를 못 따라옴).
          clip 은 잘라내되 스크롤 컨테이너가 아니라 sticky 가 바깥 스크롤 기준으로 동작한다. */}
      {canQuery() && (
        <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,overflow:'clip'}}>
          <div style={isMobile?hScroll:undefined}>
          {/* 모바일 minWidth 는 3열이 다 들어가는 최소치(320px 기기 대비 래퍼는 그대로 둔다) */}
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13,minWidth:isMobile?318:undefined}}>
            {isMobile
              ? (<colgroup><col style={{width:'58%'}} /><col style={{width:'27%'}} /><col style={{width:'15%'}} /></colgroup>)
              : (<colgroup>
                  <col style={{width:'34%'}} /><col style={{width:'14%'}} /><col style={{width:'14%'}} />
                  <col style={{width:'9%'}} /><col style={{width:'13%'}} /><col style={{width:'16%'}} />
                </colgroup>)}
            <thead ref={theadRef}>
              <tr>
                {sortTh('거래처','name')}
                {!isMobile && sortTh('사업자번호','bizNo')}
                {!isMobile && sortTh('연결 회사','companyName')}
                {!isMobile && sortTh('수수료','serviceFee',true)}
                {sortTh('미수 잔액','balance',true)}
                <th style={Object.assign({},TH,{textAlign:'right'})}>관리</th>
              </tr>
            </thead>
            <tbody>
              {shownGroups.map(function(x){ return groupRows(x); })}
              {shownDirects.map(function(r){ return unitRow(r); })}
              {shownLegacy.map(function(r){ return legacyRow(r); })}
              {shownSystem.map(function(r){ return systemRow(r); })}
              {shownCount === 0 && <tr><td colSpan={COLS} style={{textAlign:'center',color:'#94a3b8',padding:'30px 0'}}>{q.trim()?'검색 결과 없음':'등록된 거래처가 없습니다.'}</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* 데스크탑 편집 패널 — 오른쪽 슬라이드 (옛 인라인 td colSpan 행 대체) */}
      {!isMobile && form && editUnitRec && editPanel(editUnitRec.name || '거래처 수정',
        editUnitRec.isChild ? ('서류대행: ' + editUnitRec.accountName) : '직거래',
        unitEditFields(editUnitRec), unitEditActions(editUnitRec))}
      {!isMobile && form && editAccRec && editPanel(editAccRec.name || '대행사 수정',
        '대행사 · 하위 ' + editAccRec.units.length + '곳',
        accountEditFields(editAccRec), accountEditActions(editAccRec))}

      {/* 모바일 편집 시트 — 인라인 편집 행 대체 (표 가로 스크롤 밖) */}
      {isMobile && form && editUnitRec && editSheet(editUnitRec.name || '거래처 수정',
        editUnitRec.isChild ? ('서류대행: ' + editUnitRec.accountName) : '직거래',
        unitEditFields(editUnitRec), unitEditActions(editUnitRec))}
      {isMobile && form && editAccRec && editSheet(editAccRec.name || '대행사 수정',
        '대행사 · 하위 ' + editAccRec.units.length + '곳',
        accountEditFields(editAccRec), accountEditActions(editAccRec))}

      {/* 하단 스페이서 — 최하단 행이 화면 끝에 붙지 않게 (margin 은 스크롤 컨테이너에 먹히지 않아 실제 높이로, Jin 8/13 ×2) */}
      {canQuery() && <div style={{height:200}} />}

      {/* ⋯ 메뉴 바깥 클릭 닫기 */}
      {menuFor && <div onClick={function(){setMenuFor(null)}} style={{position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:200}} />}

      {/* ＋ 거래처 추가 */}
      {add && (<div style={modalWrap} onClick={function(){setAdd(null)}}>
        <div style={modalBox} onClick={function(e){e.stopPropagation()}}>
          <h3 style={{margin:'0 0 4px',fontSize:15,color:'#1B3A5C'}}>거래처 추가</h3>
          <p style={{fontSize:12,color:'#64748b',margin:'0 0 14px'}}>어떤 거래처인가요?</p>
          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr 1fr',gap:8,marginBottom:14}}>
            {typeCard('direct','','직거래 수출업체','우리와 직접 거래')}
            {typeCard('broker','','대행사','여러 업체를 중개')}
            {typeCard('sub','↳','대행사 하위업체','기존 대행사가 서류대행')}
          </div>

          {add.type === 'sub' && (<div style={Object.assign({},frmSt,{marginBottom:10})}>
            {fld('서류대행 업체', (
              <select value={add.accountId} onChange={function(e){setA('accountId',e.target.value)}} style={inSt}>
                <option value="">선택…</option>
                {brokerOptions.map(function(o){return <option key={o.id} value={o.id}>{o.name}</option>})}
              </select>
            ))}
          </div>)}

          <div style={frmSt}>
            {fld(add.type==='broker'?'대행사명':'거래처명', textIn(add.name, function(v){setA('name',v)}, '예: 부타무역'))}
            {fld('사업자번호', textIn(add.bizNo, function(v){setA('bizNo',v)}, '000-00-00000'), 150)}
            {add.type !== 'broker' && fld('주소', textIn(add.addr, function(v){setA('addr',v)}, '예: 인천 중구 …'), 260, '말소신청서 위임자란에 실립니다')}
          </div>
          <div style={Object.assign({},frmSt,{marginTop:10})}>
            {add.type !== 'broker' && fld('수수료', textIn(add.serviceFee, function(v){setA('serviceFee',v)}, '0', true), 110)}
            {add.type !== 'broker' && fld('원부', textIn(add.wonbuFee, function(v){setA('wonbuFee',v)}, '0', true), 110)}
            {add.type !== 'broker' && fld('재교부', textIn(add.reissueFee, function(v){setA('reissueFee',v)}, '0', true), 110, '0 = 대행사 기본 재교부료')}
            {fld('이월 미수 잔액', textIn(add.openingBalance, function(v){setA('openingBalance',v)}, '0', true), 140, '기존 거래처면 과거 미수 입력')}
            {add.type !== 'broker' && fld('연결 회사 (선택)', (
              <select value={add.companyId} onChange={function(e){setA('companyId',e.target.value)}} style={inSt}>
                <option value="">(미연결)</option>
                {linkable.map(function(o){return <option key={o.id} value={o.id}>{o.name}</option>})}
              </select>
            ))}
            {add.type !== 'broker' && (
              <div style={{display:'flex',flexDirection:'column',gap:3}}>
                <label style={chkSt}><input type="checkbox" checked={!!add.issueTaxInvoice}
                  onChange={function(e){setA('issueTaxInvoice',e.target.checked?1:0)}} /> 세금계산서 발행 (기본값)</label>
                <small style={capSt}>달마다 다르게 하려면 정산 › 장부 머리말에서 그 달만 바꿀 수 있습니다</small>
              </div>
            )}
            {add.type === 'broker' && fld('메모', textIn(add.memo, function(v){setA('memo',v)}, ''))}
          </div>

          <div style={Object.assign({display:'flex',gap:8,alignItems:'center',marginTop:14}, isMobile?{flexWrap:'wrap'}:null)}>
            <label style={Object.assign({display:'inline-flex',alignItems:'center',justifyContent:'center',gap:5,border:'1px solid #e2e8f0',borderRadius:8,padding:isMobile?'0 12px':'6px 12px',fontSize:isMobile?13:12.5,fontWeight:600,color:ocrLoading?'#94a3b8':'#2563EB',cursor:ocrLoading?'default':'pointer'}, isMobile?{flexBasis:'100%',minHeight:44,boxSizing:'border-box'}:null)}>
              {ocrLoading?'인식중…':'사업자등록증으로 자동입력'}
              <input type="file" accept="image/*" disabled={ocrLoading} style={{display:'none'}} onChange={function(e){var f=e.target.files&&e.target.files[0]; ocrBizLicense(f); e.target.value='';}} />
            </label>
            <div style={{flex:1}} />
            <button className="btn btn-outline btn-sm" style={btnSt} onClick={function(){setAdd(null)}}>취소</button>
            <button className="btn btn-blue btn-sm" style={btnSt} onClick={submitAdd}>추가</button>
          </div>
        </div>
      </div>)}

      {/* 소속 이동 확인 */}
      {move && (<div style={modalWrap} onClick={function(){setMove(null)}}>
        <div style={Object.assign({},modalBox,{width:480})} onClick={function(e){e.stopPropagation()}}>
          <h3 style={{margin:'0 0 4px',fontSize:15,color:'#1B3A5C'}}>
            {move.toAccountId ? (move.name+'의 서류대행을 '+move.toName+'(으)로 옮길까요?') : (move.name+'을(를) 직거래로 독립시킬까요?')}
          </h3>
          <p style={{fontSize:12,color:'#64748b',margin:'0 0 14px'}}>
            {move.toAccountId ? ('이동한 뒤의 거래는 '+move.toName+' 장부에 기록됩니다.') : '직접 관리 거래처가 됩니다 — 이동한 뒤의 거래는 이 거래처 장부에 직접 기록됩니다.'}
          </p>
          {move.balance !== 0 && (<label style={{display:'flex',alignItems:'center',gap:8,padding:isMobile?'12px':'10px 12px',border:'1.5px solid #e2e8f0',borderRadius:10,marginBottom:8,fontSize:13,cursor:'pointer',minHeight:isMobile?44:undefined,boxSizing:'border-box'}}>
            <input type="checkbox" checked={!!move.carry} onChange={function(e){var c=e.target.checked; setMove(function(p){var n=Object.assign({},p); n.carry=c; return n;});}} />
            <span>과거 미수 잔액 <b style={{color:balColor(move.balance)}}>{won(move.balance)}</b>도 함께 옮기기</span>
          </label>)}
          <ul style={{margin:'0 0 14px',paddingLeft:18}}>
            <li style={{fontSize:12.5,color:'#64748b',margin:'3px 0'}}><b>해제(기본)</b> — 과거 미수는 {move.name}이(가) 지금처럼 직접 정산. 목록에 <span style={badge('#e2e8f0','#64748b')}>종전 잔액</span> 행으로 남고, 완납되면 자동 정리됩니다.</li>
            <li style={{fontSize:12.5,color:'#64748b',margin:'3px 0'}}><b>체크</b> — 과거 장부와 미수까지 함께 이관 (금액 변동 0 검증 후 완료, 실패 시 자동 취소).</li>
          </ul>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <div style={{flex:1}} />
            <button className="btn btn-outline btn-sm" style={btnSt} onClick={function(){setMove(null)}}>취소</button>
            <button className="btn btn-blue btn-sm" style={btnSt} onClick={doMove}>이동</button>
          </div>
        </div>
      </div>)}

      {/* 대행업체 초대는 본인 몫 하위업체가 없으면 서버가 발급 때 만든다 — 닫을 때 목록 다시 읽기 */}
      {invite && <CancelAgencyInviteModal unitId={invite.id} accountId={invite.accountId} unitName={invite.name} qs={qs()} showToast={showToast} onClose={function(){ var acc = invite.accountId; setInvite(null); if(acc) load(); }} />}
      {shareOpen && canShare && <CancelAgencyShareLinkModal scope={shareScope} agencyName={agencyName} showToast={showToast} onClose={function(){ setShareOpen(false); }} />}

      {/* 대행사 승격 확인 */}
      {promote && (<div style={modalWrap} onClick={function(){setPromote(null)}}>
        <div style={Object.assign({},modalBox,{width:480})} onClick={function(e){e.stopPropagation()}}>
          <h3 style={{margin:'0 0 4px',fontSize:15,color:'#1B3A5C'}}>{promote.name}을(를) 대행사로 승격할까요?</h3>
          <p style={{fontSize:12,color:'#64748b',margin:'0 0 14px'}}>여러 업체의 말소를 중개하게 된 경우 사용합니다.</p>
          <div style={Object.assign({display:'flex',gap:10,alignItems:'center',fontSize:isMobile?13:12.5,marginBottom:12}, isMobile?{flexDirection:'column',alignItems:'stretch'}:null)}>
            <div style={{flex:1,border:'1px solid #e2e8f0',borderRadius:8,padding:10}}>
              지금<br/><b>{promote.name}</b> (직거래)<br/>
              <span style={{color:'#94a3b8'}}>미수 {won(promote.balance)}</span>
            </div>
            <div style={{color:'#94a3b8',textAlign:isMobile?'center':undefined}}>{isMobile?'↓':'→'}</div>
            <div style={{flex:1,border:'1.5px solid #2563EB',borderRadius:8,padding:10,background:'#eff6ff'}}>
              <b>{promote.name}</b> <span style={badge('#2563EB','#fff')}>대행사</span><br/>
              ↳ {promote.name} (첫 하위업체)<br/>
              <span style={{color:'#94a3b8'}}>장부·미수 전액 그대로 이관</span>
            </div>
          </div>
          <ul style={{margin:'0 0 14px',paddingLeft:18}}>
            <li style={{fontSize:12.5,color:'#64748b',margin:'3px 0'}}>과거 장부와 미수 잔액이 하위업체로 그대로 따라갑니다 (금액 변동 0 검증 후 완료).</li>
            <li style={{fontSize:12.5,color:'#64748b',margin:'3px 0'}}>승격 후 [＋ 거래처 추가 → 대행사 하위업체]로 다른 업체를 추가하세요.</li>
            <li style={{fontSize:12.5,color:'#64748b',margin:'3px 0'}}>이 작업은 되돌릴 수 없습니다.</li>
          </ul>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <div style={{flex:1}} />
            <button className="btn btn-outline btn-sm" style={btnSt} onClick={function(){setPromote(null)}}>취소</button>
            <button className="btn btn-blue btn-sm" style={btnSt} onClick={doPromote}>승격 진행</button>
          </div>
        </div>
      </div>)}
    </div>
  );
}

export default CancelAgencyClients;
